import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { auth } from '../firebase';
import { Helmet } from 'react-helmet-async';

const TradeGuitarPedals = () => {
  const { firestoreUser } = useContext(UserContext);
  const currentAuthUser = auth.currentUser;
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Scroll to top on mount only (run once)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // Empty deps - only runs on initial mount

  // Show/hide "Back to Top" button based on scroll position with throttling
  useEffect(() => {
    let timeoutId = null;
    
    const handleScroll = () => {
      // Throttle scroll events to prevent excessive re-renders
      if (timeoutId) return;
      
      timeoutId = setTimeout(() => {
        const shouldShow = window.scrollY > 300;
        setShowBackToTop(prev => prev !== shouldShow ? shouldShow : prev);
        timeoutId = null;
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Track window width for responsive button positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>How to Trade Guitar Pedals Online | PedalxChange</title>
        <meta 
          name="description" 
          content="Learn how to trade guitar pedals online with PedalxChange. List your Pedalboard, browse and send offers, save the pedals you're after, and swap safely with other musicians." 
        />
        <meta 
          name="keywords" 
          content="how to trade guitar pedals, trade guitar pedals online, swap effects pedals, trade stompboxes, pedal trading platform, guitar pedal trading guide" 
        />
        <link rel="canonical" href="https://pedalxchange.com/trade-guitar-pedals" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
        <style>{`
          .intro-paragraph {
            font-size: 1.1875rem !important;
            font-weight: 500 !important;
          }
          @media (max-width: 768px) {
            section[id] {
              padding-left: 1rem !important;
              padding-right: 1rem !important;
              margin-top: 2rem !important;
            }
            .content-list {
              margin-left: 1.25rem !important;
            }
            .intro-paragraph {
              font-size: 1.1875rem !important;
              font-weight: 500 !important;
            }
          }
        `}</style>
        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            {/* H1 Heading */}
            <h1 className="text-3xl font-bold text-gray-900" style={{ 
              textAlign: 'center', 
              fontSize: '2.25rem', 
              marginBottom: '1.5rem', 
              lineHeight: '1.2',
              color: '#4A5759',
              fontFamily: 'Nunito, sans-serif'
            }}>
              Trade Guitar Pedals Online with PedalxChange
            </h1>

            {/* Intro Paragraph */}
            <p style={{ 
              fontSize: '1.125rem', 
              lineHeight: '1.8', 
              marginBottom: '2rem',
              color: '#4A5759',
              textAlign: 'center',
              fontFamily: 'Nunito, sans-serif'
            }}>
              Trading pedals shouldn't be scary or take place in the backpages of an online forum. PedalxChange is built from the ground up for one thing: safely trading guitar pedals with other musicians, so you can explore new sounds without the risk or high costs.
            </p>

            {/* Table of Contents */}
            <nav style={{
              backgroundColor: 'rgba(201, 162, 39, 0.05)',
              borderRadius: '12px',
              padding: '1.5rem 2rem',
              marginBottom: '3rem',
              border: '1px solid rgba(201, 162, 39, 0.2)'
            }}>
              <h2 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                On This Page:
              </h2>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <li>
                  <a href="#why-trade" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    Why Trade Pedals Instead of Just Buying and Selling?
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    How Trading Works on PedalxChange
                  </a>
                </li>
                <li>
                  <a href="#trade-first" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    Trade Pedals. Not Dollars.
                  </a>
                </li>
                <li>
                  <a href="#safer-trading" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    Safer Than Random DMs and One-Off Deals
                  </a>
                </li>
                <li>
                  <a href="#who-for" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    Who PedalxChange Is For
                  </a>
                </li>
                <li>
                  <a href="#how-to-start" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    How to Start Trading Guitar Pedals in a Few Minutes
                  </a>
                </li>
                <li>
                  <a href="#faq" style={{
                    color: '#5A9ECB',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif'
                  }} className="hover:underline">
                    Common Questions About Trading Guitar Pedals Online
                  </a>
                </li>
              </ul>
            </nav>

            {/* Why Trade Section */}
            <section id="why-trade" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                Why Trade Pedals Instead of Just Buying and Selling?
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                Endless buying and selling burns time and cash. Trading lets you:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Turn unused pedals into something you'll actually play</li>
                <li style={{ marginBottom: '0.5rem' }}>Move sideways into new tones instead of paying full price, repeatedly</li>
                <li style={{ marginBottom: '0.5rem' }}>Deal directly with players who understand gear, not random strangers</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                PedalxChange keeps the focus on fair trades, safer deals, and less waste, so your money and your rig work a little smarter.
              </p>
            </section>

            {/* How Trading Works Section */}
            <section id="how-it-works" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '2rem', paddingRight: '2rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                How Trading Works on PedalxChange
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                Trading here is built around two simple ideas: what you have, and what you want.
              </p>

              <div style={{ maxWidth: '650px', margin: '0 auto' }}>
              {/* Step 1 */}
              <h3 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                1. Add your pedals to your Pedalboard
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                List the pedals you're willing to trade:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Brand and model (Boss, Walrus, EQD, etc.)</li>
                <li style={{ marginBottom: '0.5rem' }}>Condition and quirks</li>
                <li style={{ marginBottom: '0.5rem' }}>Clear photos from multiple angles</li>
                <li style={{ marginBottom: '0.5rem' }}>Any notes another gear nerd would care about</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                This builds your Pedalboard—your "outgoing" trade inventory.
              </p>

              {/* Step 2 */}
              <h3 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                2. Save the pedals you're after
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                Next, add the pedals you're hunting for:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Specific models you've been chasing</li>
                <li style={{ marginBottom: '0.5rem' }}>Types of pedals (fuzz, delay, reverb, modulation, etc.)</li>
                <li style={{ marginBottom: '0.5rem' }}>Alternatives you'd be happy with in the same lane</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                Tap the heart on any pedal, or add one from the listing page, to build your Wantlist. We notify you when one is listed.
              </p>

              {/* Step 3 */}
              <h3 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                3. Send an offer and complete the trade
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                When you see a pedal you like, you can:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Use structured offer templates to define the trade</li>
                <li style={{ marginBottom: '0.5rem' }}>Add optional notes (kept short and focused)</li>
                <li style={{ marginBottom: '0.5rem' }}>Confirm shipping and any cash difference, if needed</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                From there, you ship, track, and complete the trade—turning unused pedals into new favorites.
              </p>
              <h3 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                4. Trade matches, when there is overlap
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                Most trades start with a direct offer. Occasionally your Pedalboard and Wantlist line up with someone else's, and PedalxChange flags the trade opportunity for you.
              </p>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginBottom: '0.75rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                You'll see a trade match when:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Your pedal is on someone's Wantlist</li>
                <li style={{ marginBottom: '0.5rem' }}>One of their pedals lines up with your own Wants</li>
                <li style={{ marginBottom: '0.5rem' }}>A trade makes sense for both sides</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                When that happens, we show you both sides so you can send an offer straight away.
              </p>

              {/* Step 4 */}
              </div>
            </section>

            {/* Trade Pedals Not Dollars Section */}
            <section id="trade-first" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                Trade Pedals. Not Dollars.
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                PedalxChange is built to make trading the default, not the afterthought:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.75rem' }}><strong>Trade-first experience</strong> – Everything revolves around your Pedalboard and direct offers, not just price tags.</li>
                <li style={{ marginBottom: '0.75rem' }}><strong>Lower overall cost</strong> – You're reusing the value already sitting in your drawer, not constantly rebuying the same categories of pedals.</li>
                <li style={{ marginBottom: '0.75rem' }}><strong>Smarter discovery</strong> – Our matching engine does the watching and comparing for you, so you spend less time hunting and more time playing.</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                Instead of chasing every new release with your wallet, you move through drives, fuzzes, delays, reverbs, and modulation by trading into what actually fits your rig.
              </p>
            </section>

            {/* Safer Trading Section */}
            <section id="safer-trading" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                Safer Than Random DMs and One-Off Deals
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                Trading gear online shouldn't feel like a leap of faith. PedalxChange is designed to reduce the sketch factor:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Verified accounts and profiles</li>
                <li style={{ marginBottom: '0.5rem' }}>Secure payments handled through Stripe when cash is involved</li>
                <li style={{ marginBottom: '0.5rem' }}>Shipping labels and tracking, so you're not guessing where a pedal is</li>
                <li style={{ marginBottom: '0.5rem' }}>Optional TradeProtect-style coverage for extra peace of mind</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                You still control who you trade with and what you agree to, but the structure helps keep both sides honest.
              </p>
            </section>

            {/* Who PXC Is For Section */}
            <section id="who-for" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                Who PedalxChange Is For
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                PedalxChange is built for players who:
              </p>
              <ul className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.5rem' }}>Have a few pedals sitting unused "just in case"</li>
                <li style={{ marginBottom: '0.5rem' }}>Want to try new sounds without constantly buying and selling</li>
                <li style={{ marginBottom: '0.5rem' }}>Are tired of negotiating across multiple marketplaces and DMs</li>
                <li style={{ marginBottom: '0.5rem' }}>Care about keeping gear in circulation instead of letting it collect dust</li>
              </ul>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                If you've ever thought "this pedal is great, just not for my setup," PedalxChange is where that pedal can find its next home—and fund your next experiment.
              </p>
            </section>

            {/* How to Start Section */}
            <section id="how-to-start" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'center'
              }}>
                How to Start Trading Guitar Pedals in a Few Minutes
              </h2>
              <p className="intro-paragraph" style={{ 
                fontSize: '1.1875rem', 
                lineHeight: '1.8', 
                marginBottom: '1rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                You don't need a massive collection to get value out of the platform. Getting started is straightforward:
              </p>
              <ol className="content-list" style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                marginLeft: '1.5rem',
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                <li style={{ marginBottom: '0.75rem' }}>Create a free account on PedalxChange.</li>
                <li style={{ marginBottom: '0.75rem' }}>Add a handful of pedals you're open to trading to your Pedalboard.</li>
                <li style={{ marginBottom: '0.75rem' }}>Heart the pedals you'd like to try next to build your Wantlist.</li>
                <li style={{ marginBottom: '0.75rem' }}>Browse, send offers, and we'll tell you when a pedal from your Wantlist is listed.</li>
              </ol>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                From there, you decide when to send offers, what trades make sense, and how your board evolves over time.
              </p>
              <div style={{ textAlign: 'center' }}>
                <Link 
                  to={currentAuthUser ? '/dashboard' : '/signup'}
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#C9A227',
                    color: 'white',
                    padding: '0.875rem 2.5rem',
                    borderRadius: '9999px',
                    fontSize: '1.125rem',
                    fontWeight: 'bold',
                    fontFamily: 'Nunito, sans-serif',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#b8912a';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(201, 162, 39, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#C9A227';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(201, 162, 39, 0.3)';
                  }}
                >
                  Start Trading Pedals
                </Link>
              </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" style={{ marginBottom: '3rem', marginTop: '5rem', scrollMarginTop: '200px', paddingLeft: '4rem', paddingRight: '4rem' }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                marginBottom: '1.5rem',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Common Questions About Trading Guitar Pedals Online
              </h2>

              {/* FAQ 1 */}
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Do I have to sell, or can I trade only?
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                You can do both, but PedalxChange is built with trading first. If a straightforward sale makes more sense for a specific pedal, you can still go that route—but the platform is designed to help you get the most out of your existing gear through trades.
              </p>

              {/* FAQ 2 */}
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Is trading pedals actually cheaper than buying used?
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Most of the cost in traditional buying and selling comes from constantly reaching for new cash. Trading keeps the value in your existing pedals, so you're usually only covering small fees and shipping instead of paying full used-market prices again and again.
              </p>

              {/* FAQ 3 */}
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                What if I don't see a perfect match right away?
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8', 
                marginBottom: '1.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Most trades start with a direct offer, so browse and reach out whenever something catches your eye. Keep your Wantlist filled out too: as more players add gear, we notify you when a pedal you're after is listed, and flag a trade match if your Wantlist overlaps someone's Pedalboard. You don't have to live in your browser refreshing feeds.
              </p>

              {/* FAQ 4 */}
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Do I need a huge collection to make trading worthwhile?
              </h3>
              <p style={{ 
                fontSize: '1.0625rem', 
                lineHeight: '1.8',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif',
                textAlign: 'left'
              }}>
                No. Even a couple of pedals you're not fully in love with can be enough to start. Over time, trading lets you move into pedals that fit your rig better, without building a massive pile of "almost right" gear.
              </p>
            </section>

            {/* Final CTA Section */}
            <section style={{ 
              marginBottom: '3rem', 
              textAlign: 'center',
              padding: '3rem 2rem',
              backgroundColor: 'rgba(201, 162, 39, 0.05)',
              borderRadius: '12px'
            }}>
              <h2 style={{ 
                color: '#C9A227', 
                fontSize: '1.875rem', 
                fontWeight: 'bold', 
                marginBottom: '1rem',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Give Your Old Pedals a New Gig
              </h2>
              <p style={{ 
                fontSize: '1.125rem', 
                lineHeight: '1.8', 
                marginBottom: '2rem',
                color: '#4A5759',
                fontFamily: 'Nunito, sans-serif'
              }}>
                Pedals aren't meant to live in drawers. If you're ready to turn unused gear into new sounds—without turning every experiment into a full-price purchase—PedalxChange is built for exactly that.
              </p>
              <Link 
                to={currentAuthUser ? '/dashboard' : '/signup'}
                style={{
                  display: 'inline-block',
                  backgroundColor: '#C9A227',
                  color: 'white',
                  padding: '1rem 3rem',
                  borderRadius: '9999px',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  fontFamily: 'Nunito, sans-serif',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#b8912a';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(201, 162, 39, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#C9A227';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(201, 162, 39, 0.3)';
                }}
              >
                Sign Up and Start Trading
              </Link>
            </section>

          </div>
        </div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="back-to-top-btn"
            style={{
              position: 'fixed',
              bottom: isMobile ? '90px' : '30px',
              right: '30px',
              backgroundColor: '#5A9ECB',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4A8AB0';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#5A9ECB';
              e.target.style.transform = 'scale(1)';
            }}
            aria-label="Back to top"
          >
            ⌃
          </button>
        )}

        {/* Mobile Responsive Styles */}
        <style jsx>{`
          @media (max-width: 768px) {
            h1 {
              font-size: 1.75rem !important;
            }
            h2 {
              font-size: 1.5rem !important;
            }
            h3 {
              font-size: 1.125rem !important;
            }
            p, li {
              font-size: 1rem !important;
            }
            .max-w-7xl {
              padding-left: 1rem !important;
              padding-right: 1rem !important;
            }
          }
          
          /* Back to Top button positioning - move left on desktop only */
          @media (min-width: 769px) {
            .back-to-top-btn {
              right: 160px !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default TradeGuitarPedals;

